import { useMemo } from 'react'
import { Box, Paragraph, SelectNative } from '@alto-avios/alto-ui'
import { useTranslations } from 'next-intl'

export type ChildAge = {
  child: string
  age: number
}

type ChildrenAgePickerProps = {
  childrenAges?: ChildAge[]
  errors?: string | Array<string | undefined>
  handleSelect: (params: ChildAge) => void
  includeUnderOne?: boolean
  placeholder?: string
  unselectedAge?: number
}

export const ChildrenAgePicker = ({
  childrenAges,
  errors,
  handleSelect,
  includeUnderOne = false,
  placeholder,
  unselectedAge,
}: ChildrenAgePickerProps) => {
  const t = useTranslations('components.traveller_picker.labels')

  const hasChildren = childrenAges && childrenAges.length > 0

  const childAgesArray = useMemo(() => {
    if (!hasChildren) {
      return []
    }

    return [
      ...(includeUnderOne
        ? [
            {
              label: `${t('age_under_one')}`,
              value: '0',
            },
          ]
        : []),
      {
        label: `${t('years_old', { count: 1 })}`,
        value: '1',
      },
      ...Array.from({ length: 16 }, (_, i) => ({
        label: `${t('years_old', { count: i + 2 })}`,
        value: (i + 2).toString(),
      })),
    ]
  }, [t, hasChildren, includeUnderOne])

  if (!hasChildren) {
    return null
  }

  return (
    <Box
      data-testid="children-age-picker"
      display="flex"
      flexDirection="column"
      width="100%"
      gap="sm"
      paddingTop="sm"
    >
      <Paragraph size="sm" foregroundColor="primary">
        {t('ages_of_children')}
      </Paragraph>
      {childrenAges.map((c, i) => {
        const key = i + 1
        const errorMessage = Array.isArray(errors) ? errors[i] : errors
        return (
          <SelectNative
            key={key}
            data-testid="child-age-select"
            id={`child_${key}`}
            label={t('child_age', { count: key })}
            value={c.age === unselectedAge ? '' : c.age.toString()}
            placeholder={placeholder ?? t('select_child_age')}
            showPlaceholderInDropdown
            isRequired
            isInvalid={!!errorMessage}
            options={childAgesArray}
            onChange={(e) =>
              handleSelect({ child: `child_${key}`, age: Number(e.currentTarget.value) })
            }
            errorMessage={errorMessage}
          />
        )
      })}
    </Box>
  )
}
